import { useCallback, useMemo } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { JobType } from '@tiendanube/common';
import { RootStateType, useAppDispatch } from 'App/store';
import {
  setJobAction,
  removeJobAction,
  getJobsAction,
  cleanJobCsvDownload,
  setJobStatusAction,
  createGetBackgroundJobByIdSelector,
  checkJobStatusAction,
} from '../../jobsSlice';

export interface JobData {
  [key: string]: unknown;
}

function useBackgroundJobs(id: JobType) {
  const dispatch = useAppDispatch();
  const getBackgroundJobById = useMemo(createGetBackgroundJobByIdSelector, []);
  const job = useSelector((state: RootStateType) =>
    getBackgroundJobById(state, id),
  );

  const jobData = job?.data as JobData | undefined;

  const getJobs = useCallback(async () => {
    dispatch(getJobsAction('backgroundJobs'));
  }, [dispatch]);

  const checkJob = useCallback(async () => {
    if (
      job?.status === 'ready' ||
      job?.status === 'downloaded' ||
      job?.status === 'failed'
    ) {
      return job.status;
    } else if (job?.token) {
      const result = unwrapResult(
        await dispatch(checkJobStatusAction({ id, type: 'backgroundJobs' })),
      );
      return result[id]?.status;
    }
    return null;
  }, [dispatch, id, job?.token, job?.status]);

  const jobStatus = job?.status || 'idle';

  const csvDownloadLinkUrl = job?.url || '';

  const setJob = useCallback(
    async (token: string, data?: JobData) => {
      dispatch(
        setJobAction({
          id,
          type: 'backgroundJobs',
          token,
          ttl: 0,
          status: 'pending',
          data,
        }),
      );
    },
    [dispatch, id],
  );

  const removeJob = useCallback(async () => {
    dispatch(removeJobAction({ id, type: 'backgroundJobs' }));
  }, [dispatch, id]);

  const setFailedJob = useCallback(async () => {
    dispatch(
      setJobStatusAction({
        id,
        type: 'backgroundJobs',
        status: 'failed',
      }),
    );
  }, [dispatch, id]);

  const setDownloadedJob = useCallback(async () => {
    dispatch(
      setJobStatusAction({
        id,
        type: 'backgroundJobs',
        status: 'downloaded',
      }),
    );
  }, [dispatch, id]);

  const cleanJob = useCallback(async () => {
    dispatch(cleanJobCsvDownload());
    removeJob();
  }, [dispatch, removeJob]);

  return {
    csvDownloadLinkUrl,
    jobStatus,
    checkJob,
    getJobs,
    setJob,
    removeJob,
    setFailedJob,
    setDownloadedJob,
    cleanJob,
    job,
    jobData,
  };
}

export default useBackgroundJobs;
